import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Quotation } from '../../types/quotation';
import { Organization } from '../../types/organization';
import { Customer } from '../../types/customer';
import { Product } from '../../types/product';
import { customerDisplayName } from '../../utils/customerName';

/**
 * Modern, minimalista árajánlat PDF (A4, Inter betűtípus, teljes magyar ékezetkészlet).
 * A betűtípus külön chunkból töltődik (dynamic import), így nem terheli az alap bundle-t.
 */

const TEAL: [number, number, number] = [15, 127, 117];
const INK: [number, number, number] = [17, 24, 39];
const MUTED: [number, number, number] = [107, 114, 128];
const LINE: [number, number, number] = [229, 231, 235];
const SOFT: [number, number, number] = [243, 244, 246];
const TEAL_SOFT: [number, number, number] = [236, 248, 246];

const M = 18; // margó mm

const toDate = (v: any): Date | null => {
  if (!v) return null;
  if (typeof v === 'object' && typeof v.toDate === 'function') return v.toDate();
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
};
const fmtDate = (v: any) => {
  const d = toDate(v);
  return d ? `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getDate()).padStart(2, '0')}.` : '–';
};
const money = (n: number, currency = 'HUF') =>
  new Intl.NumberFormat('hu-HU', { style: 'currency', currency, maximumFractionDigits: currency === 'HUF' ? 0 : 2 })
    .format(n || 0)
    .replace(/ /g, ' ');
const num = (v: any) => (typeof v === 'string' ? parseFloat(v) || 0 : Number(v) || 0);

const addrLine = (a?: { street?: string; city?: string; postalCode?: string; country?: string } | null) =>
  a ? [[a.postalCode, a.city].filter(Boolean).join(' '), a.street].filter(Boolean).join(', ') : '';

async function loadImageDataUrl(url: string, timeoutMs = 4000): Promise<{ data: string; w: number; h: number } | null> {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    const res = await fetch(url, { signal: ctrl.signal, mode: 'cors' });
    clearTimeout(t);
    if (!res.ok) return null;
    const blob = await res.blob();
    const data = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
    const dims = await new Promise<{ w: number; h: number }>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = reject;
      img.src = data;
    });
    return { data, ...dims };
  } catch {
    return null;
  }
}

async function registerFonts(doc: jsPDF) {
  const { INTER_REGULAR, INTER_SEMIBOLD } = await import('./pdfFonts');
  doc.addFileToVFS('Inter-Regular.ttf', INTER_REGULAR);
  doc.addFont('Inter-Regular.ttf', 'Inter', 'normal');
  doc.addFileToVFS('Inter-SemiBold.ttf', INTER_SEMIBOLD);
  doc.addFont('Inter-SemiBold.ttf', 'Inter', 'bold');
  doc.setFont('Inter', 'normal');
}

export const generateHungarianPdf = async (
  _doc: jsPDF | null,
  quotation: Quotation,
  organization?: Organization,
  customer?: Customer,
  _products?: Product[]
): Promise<jsPDF> => {
  const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
  await registerFonts(doc);

  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const R = W - M;
  const currency = quotation.currency || quotation.items?.[0]?.currency || 'HUF';
  const number = quotation.quotationNumber || quotation.number || '';
  const items = Array.isArray(quotation.items) ? quotation.items : [];

  // ---- számítások (kedvezménnyel, ÁFA-bontással) ----
  const rows = items.map(it => {
    const qty = num(it.quantity) || 1;
    const unit = num(it.unitPrice ?? it.price);
    const disc = num(it.discount);
    const tax = num(it.taxRate);
    const net = it.lineTotal != null && num(it.lineTotal) > 0 ? num(it.lineTotal) : qty * unit * (1 - disc / 100);
    const gross = it.lineTotalWithTax != null && num(it.lineTotalWithTax) > 0 ? num(it.lineTotalWithTax) : net * (1 + tax / 100);
    return { name: it.productName || '', code: it.productCode || '', qty, unit, disc, tax, net, gross };
  });
  const subtotal = rows.reduce((s, r) => s + r.net, 0);
  const total = rows.reduce((s, r) => s + r.gross, 0);
  const taxByRate = rows.reduce<Record<string, number>>((acc, r) => {
    const k = `${r.tax}`;
    acc[k] = (acc[k] || 0) + (r.gross - r.net);
    return acc;
  }, {});

  const text = (s: string, x: number, y: number, o: { size?: number; color?: [number, number, number]; bold?: boolean; align?: 'left' | 'right' | 'center'; maxWidth?: number } = {}) => {
    doc.setFont('Inter', o.bold ? 'bold' : 'normal');
    doc.setFontSize(o.size ?? 9.5);
    doc.setTextColor(...(o.color ?? INK));
    doc.text(s, x, y, { align: o.align ?? 'left', maxWidth: o.maxWidth });
  };
  const label = (s: string, x: number, y: number, align: 'left' | 'right' = 'left') =>
    text(s.toUpperCase(), x, y, { size: 7, color: MUTED, align, bold: true });

  // ---- fejléc (minden oldalon) ----
  const logo = organization?.logoUrl ? await loadImageDataUrl(organization.logoUrl) : null;

  const drawHeader = () => {
    // vékony teal csík felül
    doc.setFillColor(...TEAL);
    doc.rect(0, 0, W, 2.5, 'F');

    let y = M + 4;
    if (logo) {
      const maxW = 42, maxH = 14;
      const ratio = Math.min(maxW / logo.w, maxH / logo.h);
      const lw = logo.w * ratio, lh = logo.h * ratio;
      try { doc.addImage(logo.data, 'PNG', M, y - 4, lw, lh); } catch { /* ignore */ }
      y += lh + 2;
    } else if (organization?.name) {
      text(organization.name, M, y + 2, { size: 13, bold: true });
      y += 8;
    }

    // jobb oldal: ÁRAJÁNLAT + sorszám
    text('ÁRAJÁNLAT', R, M + 4, { size: 9, color: MUTED, align: 'right', bold: true });
    text(number, R, M + 12, { size: 16, color: TEAL, align: 'right', bold: true });
  };

  const drawFooter = (page: number, pages: number) => {
    const y = H - 12;
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.3);
    doc.line(M, y - 5, R, y - 5);
    text('Készült a YouCRM rendszerrel. Fejleszti a Webissimo Kft.', M, y, { size: 7, color: MUTED });
    if (organization?.name) text(`${organization.name} · ${number}`, W / 2, y, { size: 7, color: MUTED, align: 'center' });
    text(`${page} / ${pages}`, R, y, { size: 7, color: MUTED, align: 'right' });
  };

  drawHeader();

  // ---- meta sáv ----
  let y = M + 24;
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.3);
  doc.line(M, y, R, y);
  y += 7;
  const metaCols = [
    ['Kelt', fmtDate(quotation.date || quotation.createdAt)],
    ['Érvényes', fmtDate(quotation.validUntil)],
    ['Fizetési határidő', quotation.paymentDue ? `${quotation.paymentDue} nap` : '–'],
    ['Pénznem', currency]
  ];
  const colW = (R - M) / metaCols.length;
  metaCols.forEach(([k, v], i) => {
    label(k, M + i * colW, y);
    text(v, M + i * colW, y + 5, { size: 10, bold: true });
  });
  y += 9;
  doc.line(M, y + 3, R, y + 3);
  y += 12;

  // ---- felek ----
  const half = (R - M - 8) / 2;
  const drawParty = (x: number, title: string, lines: { t: string; bold?: boolean; muted?: boolean }[]) => {
    label(title, x, y);
    let yy = y + 5.5;
    lines.filter(l => l.t).forEach(l => {
      text(l.t, x, yy, { size: l.bold ? 10.5 : 9, bold: !!l.bold, color: l.muted ? MUTED : INK, maxWidth: half });
      yy += l.bold ? 5.5 : 4.5;
    });
    return yy;
  };
  const issuerLines = [
    { t: organization?.name || quotation.companyName || '', bold: true },
    { t: addrLine(organization?.address) || quotation.companyAddress || '' },
    { t: (organization?.taxNumber || quotation.taxNumber) ? `Adószám: ${organization?.taxNumber || quotation.taxNumber}` : '', muted: true },
    { t: organization?.contact?.email ? organization.contact.email : '', muted: true },
    { t: organization?.contact?.phone ? organization.contact.phone : '', muted: true },
    { t: quotation.representativeName ? `Képviselő: ${quotation.representativeName}` : '', muted: true }
  ];
  const custAny = customer as any;
  const customerLines = [
    { t: customer ? customerDisplayName(customer) : '', bold: true },
    { t: customer ? addrLine(custAny.address) : '' },
    { t: custAny?.taxNumber ? `Adószám: ${custAny.taxNumber}` : '', muted: true },
    { t: custAny?.email || '', muted: true },
    { t: custAny?.phone || '', muted: true },
    { t: quotation.contactName ? `Kapcsolattartó: ${quotation.contactName}` : '', muted: true }
  ];
  const y1 = drawParty(M, 'Kiállító', issuerLines);
  const y2 = drawParty(M + half + 8, 'Ügyfél', customerLines);
  y = Math.max(y1, y2) + 8;

  // ---- tételek táblázat ----
  autoTable(doc, {
    startY: y,
    theme: 'plain',
    margin: { left: M, right: M, top: M + 30, bottom: 22 },
    head: [['#', 'Megnevezés', 'Menny.', 'Egységár', 'Kedv.', 'ÁFA', 'Nettó', 'Bruttó']],
    body: rows.map((r, i) => [
      String(i + 1),
      r.code ? `${r.name}\n${r.code}` : r.name,
      String(r.qty),
      money(r.unit, currency),
      r.disc ? `${r.disc}%` : '–',
      `${r.tax}%`,
      money(r.net, currency),
      money(r.gross, currency)
    ]),
    styles: { font: 'Inter', fontSize: 8.5, cellPadding: { top: 3, bottom: 3, left: 2, right: 2 }, textColor: INK, lineColor: LINE, lineWidth: 0, overflow: 'linebreak', valign: 'middle' },
    headStyles: { fillColor: SOFT, textColor: MUTED, fontStyle: 'bold', fontSize: 7, halign: 'left', lineWidth: 0 },
    bodyStyles: { lineWidth: { bottom: 0.2 } as any, lineColor: LINE },
    columnStyles: {
      0: { cellWidth: 7, textColor: MUTED, halign: 'left' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 14, halign: 'right' },
      3: { cellWidth: 24, halign: 'right' },
      4: { cellWidth: 13, halign: 'right', textColor: MUTED },
      5: { cellWidth: 12, halign: 'right', textColor: MUTED },
      6: { cellWidth: 25, halign: 'right' },
      7: { cellWidth: 27, halign: 'right', fontStyle: 'bold' }
    },
    didParseCell: (d) => {
      if (d.section === 'head' && d.column.index >= 2) d.cell.styles.halign = 'right';
      if (d.section === 'body' && d.column.index === 1 && rows[d.row.index]?.code) {
        // termékkód halványabban (második sor) – autoTable nem támogat sorrészenkénti stílust, ezért kisebb betű
        d.cell.styles.fontSize = 8.5;
      }
    },
    didDrawPage: (d) => {
      if (d.pageNumber > 1) drawHeader();
    }
  });
  y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

  // ---- összesítő doboz (jobbra) ----
  const boxW = 78;
  const lines = [
    ['Nettó összesen', money(subtotal, currency)],
    ...Object.entries(taxByRate)
      .sort((a, b) => Number(b[0]) - Number(a[0]))
      .map(([rate, amt]) => [`ÁFA ${rate}%`, money(amt, currency)])
  ];
  const boxH = 8 + lines.length * 6 + 12;
  if (y + boxH > H - 19) { doc.addPage(); drawHeader(); y = M + 30; }
  const bx = R - boxW;
  doc.setFillColor(...TEAL_SOFT);
  doc.roundedRect(bx, y, boxW, boxH, 2, 2, 'F');
  let ly = y + 8;
  lines.forEach(([k, v]) => {
    text(k, bx + 5, ly, { size: 8.5, color: MUTED });
    text(v, bx + boxW - 5, ly, { size: 8.5, align: 'right' });
    ly += 6;
  });
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.4);
  doc.line(bx + 5, ly - 2, bx + boxW - 5, ly - 2);
  text('Bruttó végösszeg', bx + 5, ly + 5, { size: 9, bold: true });
  text(money(total, currency), bx + boxW - 5, ly + 5, { size: 12, bold: true, color: TEAL, align: 'right' });
  y += boxH + 10;

  // ---- megjegyzés ----
  const ensure = (need: number) => { if (y + need > H - 19) { doc.addPage(); drawHeader(); y = M + 30; } };
  if (quotation.notes) {
    ensure(20);
    label('Megjegyzés', M, y);
    y += 5.5;
    const ls: string[] = doc.splitTextToSize(quotation.notes, R - M);
    doc.setFont('Inter', 'normal'); doc.setFontSize(9); doc.setTextColor(...INK);
    ls.forEach(l => { ensure(6); doc.text(l, M, y); y += 4.5; });
    y += 6;
  }

  // ---- feltételek ----
  ensure(26);
  label('Feltételek', M, y);
  y += 5.5;
  [
    `Az árajánlat a fent megjelölt időpontig${quotation.validUntil ? ` (${fmtDate(quotation.validUntil)})` : ''} érvényes.`,
    'Az árak a feltüntetett ÁFA-tartalommal értendők; az ajánlat elfogadása után az árak rögzítettek.',
    quotation.paymentDue ? `Fizetési feltétel: ${quotation.paymentDue} nap a számla kiállításától.` : 'Fizetési feltétel: a számlán feltüntetett határidő szerint.',
    'Az árajánlat aláírás és pecsét nélkül is érvényes.'
  ].forEach(l => {
    ensure(6);
    text('•', M, y, { size: 8.5, color: MUTED });
    text(l, M + 4, y, { size: 8.5, color: MUTED, maxWidth: R - M - 4 });
    y += 4.8;
  });

  // ---- lábléc minden oldalra ----
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    drawFooter(i, pages);
  }
  return doc;
};
